<!-- Layout revision: one `card cl-tight` per output feature, with the feature's configuration
     laid out full width inside the card body instead of nested in the `dd` of the toggle's
     definition list (which capped it at `calc(100% - 14rem)`). Spacing uses cl tokens
     (cl-spacing-*) instead of Bootstrap mb-3/my-3/mt-4; field groups are `fieldset` +
     `legend`. No form controls, validators or control names were changed. -->

<ng-container [formGroup]="form">

  <!-- ── Watermark ──────────────────────────────────────────────────────── -->
  <div class="row">
    <div class="col-12">
      <div class="card cl-tight">
        <div class="card-header">Watermark</div>
        <div class="card-body">

          <!-- The one control that decides whether anything is embedded: `watermark` is the key
               the template reads, so the picker sets it directly rather than deriving it from a
               separate checkbox. STARDUSTmark is the only entry because
               vod-default-cmaf-abr-v2 implements only the Castlabs single-frame watermark --
               Content Armor A/B lives on v1 and is reached through the Watermark PO flow, which
               still targets that template. -->
          <div class="row">
            <div class="col-12 col-md-6">
              <label class="form-label" for="watermark">Watermark</label>
              <select class="form-select" formControlName="watermark" id="watermark">
                <option [ngValue]="null">No Watermark</option>
                <option [ngValue]="STARDUSTMARK">STARDUSTmark</option>
              </select>
              <p class="valid-feedback">Embeds an invisible forensic watermark into the video.</p>
            </div>
          </div>

          <!-- One container for the group, so the ten settings bind as `stardustmark.*` and the
               form value already has the shape the template reads. -->
          @if (form.get('watermark').value === STARDUSTMARK) {
            <ng-container formGroupName="stardustmark">

              <fieldset class="cl-spacing-medium">
                <legend>Payload</legend>
                <div class="row">
                  <div class="col-12 col-md-6">
                    <label class="form-label" for="bit_profile">Bits embedded</label>
                    <select class="form-select" formControlName="bit_profile" id="bit_profile">
                      @for (b of sd_bit_profiles; track b) {
                        <option [ngValue]="b.id">{{ b.name }}</option>
                      }
                    </select>
                  </div>
                  <div class="col-12 col-md-6">
                    <label class="form-label" for="wm_id">ID<span class="required">*</span></label>
                    <input class="form-control" [class.invalid]="sm.get('wm_id').errors" formControlName="wm_id" id="wm_id" type="number">
                    @if (sm.get('wm_id').errors?.required) {
                      <p class="invalid-feedback">This field is required.</p>
                    }
                    @if (sm.get('wm_id').errors?.max) {
                      <p class="invalid-feedback">The maximum value is {{ sm.get('wm_id').errors.max.max }}.</p>
                    }
                    @if (sm.get('wm_id').errors?.min) {
                      <p class="invalid-feedback">Negative values are not permitted.</p>
                    }
                    @if (!sm.get('wm_id').errors) {
                      <p class="valid-feedback">A unique identifier within the selected payload size.</p>
                    }
                  </div>
                </div>
              </fieldset>

              <fieldset class="cl-spacing-medium">
                <legend>Robustness</legend>
                <div class="row">
                  <div class="col-12 col-md-4">
                    <label class="form-label" for="strength">Strength<span class="required">*</span></label>
                    <input class="form-control" [class.invalid]="sm.get('strength').errors" formControlName="strength" id="strength" max="20" min="1" step="1" type="number" />
                    <p class="valid-feedback">The default value is 4 (standard robustness).</p>
                    @if (sm.get('strength').errors?.required) {
                      <p class="invalid-feedback">This field is required.</p>
                    }
                    @if (sm.get('strength').errors?.max) {
                      <p class="invalid-feedback d-block">The maximum value is {{ sm.get('strength').errors.max.max }}.</p>
                    }
                    @if (sm.get('strength').errors?.min) {
                      <p class="invalid-feedback d-block">The minimum value is {{ sm.get('strength').errors.min.min }}.</p>
                    }
                  </div>
                  <div class="col-12 col-md-4">
                    <label class="form-label" for="fec">FEC<span class="required">*</span></label>
                    <input class="form-control" [class.invalid]="sm.get('fec').errors" formControlName="fec" id="fec" min="1" step="1" type="number" />
                    <p class="valid-feedback">Forward error correction. The default value is 2.</p>
                    @if (sm.get('fec').errors?.required) {
                      <p class="invalid-feedback">This field is required.</p>
                    }
                    @if (sm.get('fec').errors?.min) {
                      <p class="invalid-feedback">The minimum value is {{ sm.get('fec').errors.min.min }}.</p>
                    }
                  </div>
                  <div class="col-12 col-md-4">
                    <label class="form-label" for="pm_mode">Perceptual model</label>
                    <select class="form-select" formControlName="pm_mode" id="pm_mode">
                      @for (mode of sd_pm_modes; track mode.id) {
                        <option [ngValue]="mode.id">{{ mode.name }}</option>
                      }
                    </select>
                    <p class="valid-feedback valid-feedback-none"></p>
                  </div>
                </div>
              </fieldset>

              <fieldset class="cl-spacing-medium">
                <legend>Superpixel grid</legend>
                <div class="row">
                  <div class="col-12 col-md-4">
                    <label class="form-label" for="sp_width">Size (w &times; h)<span class="required">*</span></label>
                    <div class="input-group">
                      <input class="form-control" [class.invalid]="sm.get('sp_width').errors" formControlName="sp_width" id="sp_width" min="1" step="1" type="number" />
                      <span class="input-group-text" aria-hidden="true">&times;</span>
                      <input class="form-control" [class.invalid]="sm.get('sp_height').errors" formControlName="sp_height" id="sp_height" min="1" step="1" type="number" />
                    </div>
                    @if (sm.get('sp_width').errors || sm.get('sp_height').errors) {
                      <p class="invalid-feedback d-block">Both values are required and must be 1 or greater.</p>
                    } @else {
                      <p class="valid-feedback">Superpixel width and height in pixels.</p>
                    }
                  </div>
                  <div class="col-12 col-md-4">
                    <label class="form-label" for="superpixel_density">Superpixel density</label>
                    <select class="form-select" formControlName="superpixel_density" id="superpixel_density">
                      @for (d of densities; track d) {
                        <option [ngValue]="d.id">{{ d.name }}</option>
                      }
                    </select>
                  </div>
                  <div class="col-12 col-md-4">
                    <label class="form-label" for="pixel_density">Pixel density</label>
                    <select class="form-select" formControlName="pixel_density" id="pixel_density">
                      @for (d of densities; track d) {
                        <option [ngValue]="d.id">{{ d.name }}</option>
                      }
                    </select>
                  </div>
                </div>
              </fieldset>

              <fieldset class="cl-spacing-medium">
                <legend>Seed</legend>
                <div class="form-check cl-spacing-none">
                  <input class="form-check-input" formControlName="seed_auto" id="seed_auto" type="checkbox">
                  <label class="form-check-label" for="seed_auto">Derive the seed from the image content</label>
                </div>
                @if (!sm.get('seed_auto')!.value) {
                  <div class="row">
                    <div class="col-12 col-md-4">
                      <label class="form-label" for="seed">Seed<span class="required">*</span></label>
                      <input class="form-control" [class.invalid]="sm.get('seed').errors" formControlName="seed" id="seed" max="31" min="0" type="number">
                      @if (sm.get('seed')!.errors?.required) {
                        <p class="invalid-feedback">This field is required.</p>
                      }
                      @if (!sm.get('seed')!.errors) {
                        <p class="valid-feedback">Enter a seed value between 0 and 31.</p>
                      }
                      @if (sm.get('seed')!.errors?.max) {
                        <p class="invalid-feedback d-block">The maximum value is 31.</p>
                      }
                      @if (sm.get('seed')!.errors?.min) {
                        <p class="invalid-feedback d-block">The minimum value is 0.</p>
                      }
                    </div>
                  </div>
                }
              </fieldset>

            </ng-container>
          }
        </div>
      </div>
    </div>
  </div>

  <!-- ── Encryption ─────────────────────────────────────────────────────── -->
  <div class="row">
    <div class="col-12">
      <div class="card cl-tight">
        <div class="card-header">Encryption</div>
        <div class="card-body">

          <app-dl-checkbox
            checkboxId="encryptCheckbox"
            label="Enable encryption"
            description="Encrypts the output with CENC and registers the content keys with DRMtoday."
            formControlName="encrypt"
          ></app-dl-checkbox>

          @if (form.get('encrypt')!.value) {
            <fieldset class="cl-spacing-small">
              <legend>DRM systems</legend>
              <p class="valid-feedback">
                Which systems the packager advertises. Leave a system out when the delivery does not
                license it — a client that finds a PSSH it cannot get a licence for fails there,
                rather than falling through to one it could have used.
              </p>
              <div class="row">
                @for (ps of protection_systems; track ps.id) {
                  <div class="col-12 col-md-4">
                    <div class="form-check cl-spacing-none">
                      <input
                        (change)="toggleProtectionSystem(ps.id, $any($event.target).checked)"
                        [checked]="hasProtectionSystem(ps.id)"
                        class="form-check-input"
                        id="ps_{{ps.id}}"
                        type="checkbox">
                      <label class="form-check-label" for="ps_{{ps.id}}">{{ ps.name }}</label>
                    </div>
                    <p class="valid-feedback">{{ ps.description }}</p>
                  </div>
                }
              </div>
              @if (form.get('protection_systems').errors?.noProtectionSystem) {
                <p class="invalid-feedback d-block">
                  Pick at least one. An encrypted output that advertises nothing is unplayable, not
                  unprotected — turn encryption off instead.
                </p>
              }
            </fieldset>

            <fieldset class="cl-spacing-small">
              <legend>DRMtoday Onboard</legend>
              <div class="form-check cl-spacing-none">
                <input class="form-check-input" formControlName="onboard" id="onboardCheckbox" type="checkbox">
                <label class="form-check-label" for="onboardCheckbox">Also ship the keys to DRMtoday Onboard</label>
              </div>
              <p class="valid-feedback">Onboard is the licence server that runs on the aircraft, for playback without a connection to the ground.</p>

              @if (form.get('onboard')!.value) {
                <div class="row">
                  <div class="col-12">
                    <label class="form-label" for="onboard_tags">Onboard tags</label>
                    <input class="form-control cl-field-sans" formControlName="onboard_tags" id="onboard_tags" placeholder="tag1, tag2, tag3" type="text" />
                    <p class="valid-feedback">Separate tags with commas.</p>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12 col-md-6">
                    <label class="form-label" for="onboard_window_start">Onboard window start</label>
                    <input class="form-control" formControlName="onboard_window_start" id="onboard_window_start" placeholder="2026-01-01T00:00Z" type="datetime-local" />
                    <p class="valid-feedback">Start time in ISO 8601 format.</p>
                  </div>
                  <div class="col-12 col-md-6">
                    <label class="form-label" for="onboard_window_end">Onboard window end</label>
                    <input class="form-control" formControlName="onboard_window_end" id="onboard_window_end" placeholder="2030-12-31T23:59Z" type="datetime-local" />
                    <p class="valid-feedback">End time in ISO 8601 format.</p>
                  </div>
                </div>
              }
            </fieldset>
          }
        </div>
      </div>
    </div>
  </div>

  <!-- ── Thumbnails ─────────────────────────────────────────────────────── -->
  <div class="row">
    <div class="col-12">
      <div class="card cl-tight">
        <div class="card-header">Thumbnails</div>
        <div class="card-body">
          <app-dl-checkbox
            checkboxId="thumbnailsCheckbox"
            label="Generate thumbnails"
            description="Generates thumbnail images for the video. Applies to every item of this PO."
            formControlName="thumbs"
          ></app-dl-checkbox>
        </div>
      </div>
    </div>
  </div>

</ng-container>
